import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import Layout from '../../Layout';
import api from '../../api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/userContext';
import { LEGAL_UPDATE_DEPARTMENT_OPTIONS } from '../../constants/departments';
import { LEGAL_UPDATE_REPORT_MODES } from '../../utils/legalUpdateReportLabels';

const STORAGE_KEY = 'legalUpdateReportFilters.v1';

const CONTENT_TYPES = [
  'Circular',
  'Notification',
  'FAQ',
  'Other',
  'Summary- Compliance',
  'Summary- Informative',
  'Summary- Policy/ System Change',
  'Surveillance & Other Operational Circulars',
  'Mock Trading',
  'Settlement Updates',
  'Listing & Suspension  Updates',
  'Commodity Updates',
  'Not Relevant Updates',
  'Consultation Paper',
  'Orders',
  'Others',
  'Approved Securities/ Banks',
];

const SORT_OPTIONS = [
  { value: '-issueDate', label: 'Issue Date (Newest first)' },
  { value: 'issueDate', label: 'Issue Date (Oldest first)' },
];

const TRACKER_STATUS_OPTIONS = [
  'ALL',
  'Open',
  'Pending for Review',
  'Pending for Discrepancy',
  'Completed',
  'Delayed Compliance',
  'Not Applicable',
];

const defaultFilters = {
  q: '',
  issueFrom: '',
  issueTo: '',
  deadlineFrom: '',
  deadlineTo: '',
  trackerDueFrom: '',
  trackerDueTo: '',
  lawArea: [],
  contentType: 'ALL',
  criticalityLevel: 'ALL',
  sortBy: '-issueDate',
  trackerStatus: 'ALL',
  exportMode: 'complianceDeadline',
  departments: [],
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#fbfcfc',
    '& fieldset': { borderColor: 'rgba(15, 23, 42, 0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(27, 94, 32, 0.45)' },
    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
  },
};

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      mb: 1,
      mt: 0.5,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      color: '#64748b',
    }}
  >
    {children}
  </Typography>
);

const LegalUpdateReportFilterPage = () => {
  const navigate = useNavigate();
  const [{ user }] = useAuth();
  const isComplianceUser = Array.isArray(user?.departments)
    && user.departments.some((dep) => String(dep || '').trim().toLowerCase() === 'compliance');
  const [lawAreas, setLawAreas] = useState([]);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return defaultFilters;
      const parsed = { ...defaultFilters, ...JSON.parse(saved) };
      if (!parsed.contentType) parsed.contentType = 'ALL';
      if (!parsed.criticalityLevel) parsed.criticalityLevel = 'ALL';
      if (!parsed.trackerStatus) parsed.trackerStatus = 'ALL';
      if (!Array.isArray(parsed.departments)) parsed.departments = [];
      return parsed;
    } catch {
      return defaultFilters;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    const loadLawAreas = async () => {
      try {
        const lawAreaRes = await api.get('tracker/law-area');
        const areas = Array.from(
          new Set(
            (lawAreaRes?.data?.lawareas || [])
              .map((item) => item?.law_area)
              .filter(Boolean)
          )
        );
        setLawAreas(areas);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load law areas');
      }
    };
    loadLawAreas();
  }, []);

  const setField = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const setReportMode = (_event, exportMode) => {
    if (!exportMode) return;
    setFilters((prev) => ({
      ...prev,
      exportMode,
      ...(exportMode === 'complianceDeadline'
        ? { trackerDueFrom: '', trackerDueTo: '', trackerStatus: 'ALL' }
        : {}),
    }));
  };

  const resetAll = () => setFilters(defaultFilters);

  const validateDateWindow = (from, to, label) => {
    if (from && to && new Date(from) > new Date(to)) {
      toast.error(`${label}: "From" date cannot be after "To" date.`);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateDateWindow(filters.issueFrom, filters.issueTo, 'Issue date')) return;
    if (!validateDateWindow(filters.deadlineFrom, filters.deadlineTo, 'Last date to comply')) return;
    if (
      filters.exportMode === 'trackerDueDate' &&
      !validateDateWindow(filters.trackerDueFrom, filters.trackerDueTo, 'Task due date')
    ) return;
    navigate('/reports/legal-update-results', { state: { filters } });
  };

  const handleLawAreaChange = (event) => {
    const value = event.target.value;
    if (value.includes('ALL') && value.length === 1) {
      setField('lawArea', ['ALL']);
      return;
    }
    setField('lawArea', value.filter((item) => item !== 'ALL'));
  };

  const isTrackerMode = filters.exportMode === 'trackerDueDate';
  const activeMode = LEGAL_UPDATE_REPORT_MODES[filters.exportMode] || LEGAL_UPDATE_REPORT_MODES.complianceDeadline;

  return (
    <Layout title="Legal Update Report">
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          px: 2,
          background: 'linear-gradient(180deg, #f7f8f8 0%, #eef2f1 100%)',
        }}
      >
        <Container maxWidth="md" disableGutters sx={{ maxWidth: '860px !important' }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.6rem' }, color: '#0f172a' }}>
              Legal Update Report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
              Choose whether you want the list of updates, or the tasks created from them.
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: '18px',
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)',
              overflow: 'hidden',
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ p: { xs: 2.25, md: 3 } }}>
              <SectionLabel>What do you want to see?</SectionLabel>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={filters.exportMode}
                onChange={setReportMode}
                sx={{
                  mb: 0.75,
                  gap: 1,
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid rgba(15,23,42,0.12) !important',
                    borderRadius: '12px !important',
                    mx: 0,
                    textTransform: 'none',
                    py: 1.1,
                    fontWeight: 700,
                    color: '#334155',
                  },
                  '& .Mui-selected': {
                    color: '#14532d !important',
                    bgcolor: 'rgba(46,125,50,0.10) !important',
                    borderColor: '#2e7d32 !important',
                  },
                }}
              >
                <ToggleButton value="complianceDeadline">
                  <ArticleOutlinedIcon sx={{ mr: 1, fontSize: 18 }} />
                  {LEGAL_UPDATE_REPORT_MODES.complianceDeadline.title}
                </ToggleButton>
                <ToggleButton value="trackerDueDate">
                  <AssignmentTurnedInOutlinedIcon sx={{ mr: 1, fontSize: 18 }} />
                  {LEGAL_UPDATE_REPORT_MODES.trackerDueDate.title}
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
                {activeMode.helper}
              </Typography>

              <SectionLabel>Search & dates</SectionLabel>
              <Grid container spacing={1.75}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search title, summary or update number"
                    value={filters.q}
                    onChange={(e) => setField('q', e.target.value)}
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Issue date from"
                    type="date"
                    value={filters.issueFrom}
                    onChange={(e) => setField('issueFrom', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Issue date to"
                    type="date"
                    value={filters.issueTo}
                    onChange={(e) => setField('issueTo', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`${LEGAL_UPDATE_REPORT_MODES.complianceDeadline.dateLabel} from`}
                    type="date"
                    value={filters.deadlineFrom}
                    onChange={(e) => setField('deadlineFrom', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`${LEGAL_UPDATE_REPORT_MODES.complianceDeadline.dateLabel} to`}
                    type="date"
                    value={filters.deadlineTo}
                    onChange={(e) => setField('deadlineTo', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Grid>
                {isTrackerMode && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`${LEGAL_UPDATE_REPORT_MODES.trackerDueDate.dateLabel} from`}
                        type="date"
                        value={filters.trackerDueFrom}
                        onChange={(e) => setField('trackerDueFrom', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`${LEGAL_UPDATE_REPORT_MODES.trackerDueDate.dateLabel} to`}
                        type="date"
                        value={filters.trackerDueTo}
                        onChange={(e) => setField('trackerDueTo', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={fieldSx}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Box sx={{ mt: 2.5 }}>
                <SectionLabel>Narrow the list</SectionLabel>
                <Grid container spacing={1.75}>
                  <Grid item xs={12} sm={isComplianceUser ? 6 : 12}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel>Law areas</InputLabel>
                      <Select
                        multiple
                        value={filters.lawArea}
                        onChange={handleLawAreaChange}
                        input={<OutlinedInput label="Law areas" />}
                        renderValue={(selected) => (selected.includes('ALL') ? 'All law areas' : selected.join(', '))}
                      >
                        <MenuItem value="ALL">
                          <Checkbox checked={filters.lawArea.includes('ALL')} />
                          <ListItemText primary="All law areas" />
                        </MenuItem>
                        {lawAreas.map((area) => (
                          <MenuItem key={area} value={area}>
                            <Checkbox checked={filters.lawArea.includes(area)} />
                            <ListItemText primary={area} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {isComplianceUser && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" sx={fieldSx}>
                        <InputLabel>Departments</InputLabel>
                        <Select
                          multiple
                          value={filters.departments || []}
                          onChange={(e) => setField('departments', e.target.value)}
                          input={<OutlinedInput label="Departments" />}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {LEGAL_UPDATE_DEPARTMENT_OPTIONS.map((department) => (
                            <MenuItem key={department} value={department}>
                              <Checkbox checked={(filters.departments || []).includes(department)} />
                              <ListItemText primary={department} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={isTrackerMode ? 3 : 4}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel>Content type</InputLabel>
                      <Select
                        value={filters.contentType}
                        label="Content type"
                        onChange={(e) => setField('contentType', e.target.value)}
                      >
                        <MenuItem value="ALL">All content types</MenuItem>
                        {CONTENT_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={isTrackerMode ? 3 : 4}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel>Criticality</InputLabel>
                      <Select
                        value={filters.criticalityLevel}
                        label="Criticality"
                        onChange={(e) => setField('criticalityLevel', e.target.value)}
                      >
                        <MenuItem value="ALL">All criticalities</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {isTrackerMode && (
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small" sx={fieldSx}>
                        <InputLabel>Task status</InputLabel>
                        <Select
                          value={filters.trackerStatus}
                          label="Task status"
                          onChange={(e) => setField('trackerStatus', e.target.value)}
                        >
                          {TRACKER_STATUS_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={isTrackerMode ? 3 : 4}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel>Sort by</InputLabel>
                      <Select
                        value={filters.sortBy}
                        label="Sort by"
                        onChange={(e) => setField('sortBy', e.target.value)}
                      >
                        {SORT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              justifyContent="flex-end"
              sx={{
                px: { xs: 2.25, md: 3 },
                py: 2,
                bgcolor: '#f8faf9',
                borderTop: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Button
                variant="text"
                startIcon={<RestartAltIcon />}
                onClick={resetAll}
                sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}
              >
                Clear filters
              </Button>
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleSubmit}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: '10px',
                  px: 2.75,
                  py: 1,
                  color: '#fff',
                  background: '#1b5e20',
                  boxShadow: 'none',
                  '&:hover': { background: '#14532d', boxShadow: 'none' },
                }}
              >
                Generate {activeMode.short} report
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Layout>
  );
};

export default LegalUpdateReportFilterPage;
